import { LANDING_NAV_ANCHORS } from './landingCopy';

export type LandingSectionId = (typeof LANDING_NAV_ANCHORS)[number]['id'];

type LandingWindow = {
  location?: { hash: string; pathname: string; search: string };
  history?: { pushState: (data: unknown, unused: string, url?: string) => void };
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
  setTimeout?: (handler: () => void, timeout?: number) => number;
  clearTimeout?: (id: number) => void;
};

const SECTION_IDS = new Set<string>(LANDING_NAV_ANCHORS.map((anchor) => anchor.id));

function webWindow(): LandingWindow {
  return globalThis as LandingWindow;
}

export function isLandingSectionId(value: string): value is LandingSectionId {
  return SECTION_IDS.has(value);
}

export function parseLandingHash(hash: string): LandingSectionId | null {
  const id = hash.startsWith('#') ? hash.slice(1) : hash;
  return isLandingSectionId(id) ? id : null;
}

export function getLandingHash(): LandingSectionId | null {
  const hash = webWindow().location?.hash;
  return hash ? parseLandingHash(hash) : null;
}

export function setLandingHash(id: LandingSectionId): void {
  const g = webWindow();
  if (!g.location || !g.history?.pushState) return;
  const next = `#${id}`;
  if (g.location.hash === next) return;
  g.history.pushState(null, '', `${g.location.pathname}${g.location.search}${next}`);
}

export function landingHref(id: LandingSectionId): string {
  return `#${id}`;
}

/** Web: apply current hash now, on back/forward, and once after layout. */
export function subscribeLandingHash(apply: () => void): () => void {
  const g = webWindow();
  if (!g.addEventListener || !g.removeEventListener) {
    return () => undefined;
  }
  apply();
  g.addEventListener('hashchange', apply);
  g.addEventListener('popstate', apply);
  const retry = g.setTimeout?.(apply, 120) ?? 0;
  return () => {
    g.removeEventListener('hashchange', apply);
    g.removeEventListener('popstate', apply);
    g.clearTimeout?.(retry);
  };
}
